import { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Gift, Truck, Plus, Check, X } from "lucide-react";
import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";

/**
 * V3.1 replacement for CompleteRoutine — a bundle builder with a live
 * milestone progress bar and tiered rewards. Left column keeps the
 * sticky editorial image (shorter now so the Bundle Deck sits directly
 * beneath in the same sticky container). Right column reuses the V2
 * horizontal product-row layout but the Quick-add button toggles a
 * product into/out of the bundle.
 *
 * Reward tiers (unlocked as products are added):
 *   • 2 items → 10% off entire bundle
 *   • 3 items → Free Gentle Cleanser ($34 value)
 *   • 5 items → Free Anti-Aging Serum ($79 value) + Free shipping
 *
 * State is intentionally kept in local React state only — no cart API,
 * no localStorage — so the demo resets between visits and stays simple.
 */
const parsePrice = (s) => parseFloat(String(s || "0").replace(/[^0-9.]/g, "")) || 0;

const TIERS = [
  { count: 2, label: "10% off bundle", icon: Sparkles },
  { count: 3, label: "+ Free Gentle Cleanser", subtle: "$34 value", icon: Gift },
  { count: 5, label: "+ Free Anti-Aging Serum & shipping", subtle: "$79 value", icon: Truck },
];
const MAX = TIERS[TIERS.length - 1].count;

// PDP3.2 reward tiers — mirror riversol.com/products/bundle-save "Your custom set":
// 2 → 10% off, 3 → free Hyaluronic + PDRN Hydrogel gift, 5 → 25% off.
const TIERS_V32 = [
  { count: 2, label: "10% off bundle", icon: Sparkles },
  { count: 3, label: "Free Hyaluronic + PDRN gift", icon: Gift },
  { count: 5, label: "25% off bundle", icon: Sparkles },
];
const GIFT_V32 = {
  name: "Hyaluronic Acid + PDRN Hydrogel Mask",
  image:
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_e3513a08-18b3-459d-901e-2457595c9150/artifacts/2jk1dxzb_Mak-hidrogel.png",
  unlockAt: 3,
};
const discountForV32 = (count) => (count >= 5 ? 0.25 : count >= 2 ? 0.1 : 0);

const BundleDeck = ({ selected, products, onRemove }) => {
  const count = selected.size;
  const items = useMemo(
    () => [...selected].map((i) => products[i]).filter(Boolean),
    [selected, products]
  );
  const subtotal = useMemo(
    () => items.reduce((s, p) => s + parsePrice(p.price), 0),
    [items]
  );
  const tierIdx = TIERS.reduce((acc, t, i) => (count >= t.count ? i : acc), -1);
  const discountPct = tierIdx >= 0 ? 0.10 : 0;
  const total = subtotal * (1 - discountPct);
  const nextTier = TIERS.find((t) => count < t.count);
  const progressPct = Math.min((count / MAX) * 100, 100);

  // Trigger one-shot celebration when a new tier is crossed.
  const prevTier = useRef(tierIdx);
  const [flash, setFlash] = useState(false);
  useEffect(() => {
    if (tierIdx > prevTier.current) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 900);
      return () => clearTimeout(t);
    }
    prevTier.current = tierIdx;
  }, [tierIdx]);

  return (
    <div
      className={`bg-white border border-neutral-200 p-6 transition-shadow duration-500 ${
        flash ? "shadow-[0_0_0_2px_rgba(185,169,106,0.55)]" : "shadow-none"
      }`}
      data-testid="bundle-deck"
    >
      <div className="flex items-baseline justify-between mb-4">
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-500">
          Your bundle
        </span>
        <span className="font-mono text-[11px] tabular-nums text-neutral-900" data-testid="bundle-count">
          {count} / {MAX} items
        </span>
      </div>

      {/* Progress bar with 3 milestone notches. */}
      <div className="relative mb-3" data-testid="bundle-progress">
        <div className="h-1.5 bg-neutral-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-riversol-blue"
            initial={false}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>
        {TIERS.map((t) => {
          const pos = (t.count / MAX) * 100;
          const unlocked = count >= t.count;
          return (
            <span
              key={t.count}
              className={`absolute -top-[3px] w-3 h-3 rounded-full border-2 transition-colors duration-300 ${
                unlocked
                  ? "bg-riversol-blue border-riversol-blue"
                  : "bg-white border-neutral-300"
              }`}
              style={{ left: `calc(${pos}% - 6px)` }}
              aria-label={`${t.count} items — ${t.label}`}
            />
          );
        })}
      </div>

      {/* Reward tier chips */}
      <ul className="grid grid-cols-3 gap-2 mb-5" data-testid="bundle-tiers">
        {TIERS.map((t, i) => {
          const Icon = t.icon;
          const unlocked = count >= t.count;
          return (
            <li
              key={t.count}
              className={`flex flex-col gap-1 text-center transition-colors ${
                unlocked ? "text-riversol-blue" : "text-neutral-400"
              }`}
              data-testid={`bundle-tier-${t.count}`}
            >
              <div
                className={`mx-auto flex items-center justify-center w-8 h-8 rounded-full transition-all ${
                  unlocked ? "bg-riversol-blue/10" : "bg-neutral-100"
                }`}
              >
                <Icon size={14} strokeWidth={1.75} />
              </div>
              <span className="font-mono text-[9.5px] tracking-[0.14em] uppercase leading-tight">
                {t.count} items
              </span>
              <span className="text-[10.5px] leading-tight px-1">
                {t.label}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Selected chips */}
      {items.length > 0 && (
        <div className="border-t border-neutral-200 pt-4 mb-4">
          <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-neutral-500 mb-2">
            In your bundle
          </div>
          <ul className="flex flex-wrap gap-2" data-testid="bundle-chips">
            <AnimatePresence initial={false}>
              {items.map((p) => {
                const idx = products.indexOf(p);
                return (
                  <motion.li
                    key={p.name}
                    initial={{ opacity: 0, scale: 0.85 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.85 }}
                    transition={{ duration: 0.2 }}
                    className="inline-flex items-center gap-1.5 bg-neutral-100 rounded-full pl-3 pr-1.5 py-1 text-[11.5px] text-neutral-800"
                  >
                    <span className="truncate max-w-[130px]">{p.name}</span>
                    <button
                      type="button"
                      onClick={() => onRemove(idx)}
                      aria-label={`Remove ${p.name}`}
                      className="w-4 h-4 flex items-center justify-center rounded-full hover:bg-neutral-300 transition-colors"
                    >
                      <X size={10} strokeWidth={2} />
                    </button>
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>
        </div>
      )}

      {/* Subtotal */}
      <div className="flex items-baseline justify-between mb-5">
        <span className="text-[13px] text-neutral-500">Subtotal</span>
        <div className="flex items-baseline gap-2">
          {discountPct > 0 && (
            <span className="text-[12px] text-neutral-400 line-through font-mono tabular-nums" data-testid="bundle-subtotal-strike">
              ${subtotal.toFixed(2)}
            </span>
          )}
          <span
            className="text-[18px] font-medium text-neutral-900 font-mono tabular-nums"
            data-testid="bundle-total"
          >
            ${total.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Next-tier reminder */}
      {nextTier && (
        <p className="text-[12px] text-neutral-500 mb-4 leading-snug" data-testid="bundle-next-tier">
          Add {nextTier.count - count} more for{" "}
          <span className="text-riversol-blue font-medium">{nextTier.label.replace(/^\+ /, "")}</span>
          {nextTier.subtle && <span className="text-neutral-400"> — {nextTier.subtle}</span>}
        </p>
      )}
      {!nextTier && (
        <p className="text-[12px] text-riversol-blue mb-4 font-medium" data-testid="bundle-maxed">
          All rewards unlocked — nice bundle.
        </p>
      )}

      <button
        type="button"
        disabled={count === 0}
        data-testid="bundle-add-to-cart"
        className={`w-full py-3.5 rounded-full text-xs tracking-[0.12em] uppercase font-medium transition-colors ${
          count > 0
            ? "bg-riversol-blue text-white hover:bg-riversol-blueLight"
            : "bg-neutral-100 text-neutral-400 cursor-not-allowed"
        }`}
      >
        {count > 0 ? `Add bundle to cart — $${total.toFixed(2)}` : "Start your bundle →"}
      </button>

      <p className="text-[11px] text-neutral-400 mt-3 text-center leading-snug">
        Members save an average of $47 per bundle.
      </p>
    </div>
  );
};

const CORAL = "#E2654F";

// One selection slot in the "Your custom set" grid. Filled slots show the
// picked product's thumbnail (tap to remove); empty slots show the slot
// number + "Select an item". Milestone slots get a dashed border + a coral
// "Unlock…" badge that turns solid once the tier is reached.
const SetSlot = ({ n, product, onRemove, badge, unlocked, fixed = false }) => {
  const milestone = !!badge;
  return (
    <div className="relative" data-testid={`set-slot-${n}`}>
      <div
        className={`relative aspect-square bg-neutral-50 overflow-hidden transition-colors ${
          product
            ? "border border-riversol-blue"
            : milestone
            ? "border-2 border-dashed"
            : "border border-neutral-200"
        }`}
        style={
          milestone && !product
            ? { borderColor: unlocked ? "var(--brand-dark, #003646)" : `${CORAL}80` }
            : undefined
        }
      >
        {product ? (
          <>
            <img
              src={product.image}
              alt={product.name}
              className="w-full h-full object-cover"
            />
            {!fixed && (
              <button
                type="button"
                onClick={onRemove}
                aria-label={`Remove ${product.name}`}
                data-testid={`set-slot-remove-${n}`}
                className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center rounded-full bg-white/90 text-neutral-700 hover:bg-white shadow-sm transition-colors"
              >
                <X size={13} strokeWidth={2.2} />
              </button>
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
            <span className="text-[22px] font-light text-neutral-300 leading-none mb-2">
              {n}
            </span>
            <span className="text-[11px] text-neutral-400">Select an item</span>
          </div>
        )}
      </div>
      {badge && (
        <div className="flex justify-center">
          <span
            className="relative -mt-2 inline-block rounded-sm px-2.5 py-1 text-[9.5px] font-medium tracking-[0.06em] uppercase text-white whitespace-nowrap"
            style={{ backgroundColor: unlocked ? "var(--brand-dark, #003646)" : CORAL }}
            data-testid={`set-slot-badge-${n}`}
          >
            {unlocked ? "Unlocked" : badge}
          </span>
        </div>
      )}
    </div>
  );
};

// PDP3.2 "Your custom set" panel — replaces the image + BundleDeck on the
// left. Mirrors riversol.com/products/bundle-save: SAVE eyebrow, heading,
// dynamic "add N more" line, a 3-col slot grid (5 slots + a free-gift cell)
// and an Add To Cart button.
const CustomSetPanel = ({ selected, products, onRemove, baseProduct, hideEyebrow = false }) => {
  const selectedIdx = baseProduct ? [null, ...selected] : [...selected];
  const items = baseProduct
    ? [baseProduct, ...[...selected].map((i) => products[i]).filter(Boolean)]
    : selectedIdx.map((i) => products[i]).filter(Boolean);
  const count = items.length;
  const subtotal = items.reduce((s, p) => s + parsePrice(p.price), 0);
  const discountPct = discountForV32(count);
  const total = subtotal * (1 - discountPct);
  const giftUnlocked = count >= GIFT_V32.unlockAt;

  const nextLine = () => {
    if (count < 2)
      return { need: 2 - count, reward: "10% off", noun: count === 1 ? "product" : "products" };
    if (count < GIFT_V32.unlockAt)
      return { need: GIFT_V32.unlockAt - count, reward: "a free gift", noun: "product" };
    if (count < 5)
      return { need: 5 - count, reward: "25% off", noun: 5 - count === 1 ? "product" : "products" };
    return null;
  };
  const nl = nextLine();

  return (
    <div className="bg-white border border-neutral-200 p-6 lg:p-8" data-testid="custom-set-panel">
      {!hideEyebrow && (
        <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-neutral-400 mb-3">
          Save
        </div>
      )}
      <h3 className="text-3xl lg:text-4xl tracking-tight font-semibold text-neutral-900 mb-3">
        Your custom set
      </h3>
      <p className="text-[14px] text-neutral-500 mb-7 leading-relaxed" data-testid="custom-set-subtext">
        {nl ? (
          <>
            Add <span className="font-medium text-neutral-800">{nl.need} more</span> {nl.noun} to
            unlock <span className="font-medium text-neutral-800">{nl.reward}</span>.
          </>
        ) : (
          <>You've unlocked every reward — nice set.</>
        )}
      </p>

      {/* 3-col grid: [1][2][gift] · [3][4][5] */}
      <div className="grid grid-cols-3 gap-3 lg:gap-4 mb-8" data-testid="custom-set-grid">
        <SetSlot n={1} product={items[0]} onRemove={() => onRemove(selectedIdx[0])} fixed={!!baseProduct} />
        <SetSlot
          n={2}
          product={items[1]}
          onRemove={() => onRemove(selectedIdx[1])}
          badge="Unlock 10% off"
          unlocked={count >= 2}
        />
        {/* Free-gift cell */}
        <div className="relative" data-testid="set-slot-gift">
          <div
            className="relative aspect-square bg-neutral-50 overflow-hidden border-2 border-dashed"
            style={{ borderColor: giftUnlocked ? "var(--brand-dark, #003646)" : `${CORAL}80` }}
          >
            <img
              src={GIFT_V32.image}
              alt={GIFT_V32.name}
              className={`w-full h-full object-cover transition-all duration-500 ${
                giftUnlocked ? "" : "grayscale opacity-60"
              }`}
            />
            <div className="absolute inset-x-0 bottom-0 bg-white/85 backdrop-blur-sm px-2 py-1.5">
              <span className="block text-[9px] leading-tight tracking-[0.04em] uppercase text-neutral-600 text-center">
                Hyaluronic + PDRN Hydrogel
              </span>
            </div>
          </div>
          <div className="flex justify-center">
            <span
              className="relative -mt-2 inline-block rounded-sm px-2.5 py-1 text-[9.5px] font-medium tracking-[0.06em] uppercase text-white whitespace-nowrap"
              style={{ backgroundColor: giftUnlocked ? "var(--brand-dark, #003646)" : CORAL }}
              data-testid="set-slot-gift-badge"
            >
              {giftUnlocked ? "Free gift added" : "Unlock a free gift"}
            </span>
          </div>
        </div>
        <SetSlot n={3} product={items[2]} onRemove={() => onRemove(selectedIdx[2])} />
        <SetSlot n={4} product={items[3]} onRemove={() => onRemove(selectedIdx[3])} />
        <SetSlot
          n={5}
          product={items[4]}
          onRemove={() => onRemove(selectedIdx[4])}
          badge="Unlock 25% off"
          unlocked={count >= 5}
        />
      </div>

      {count > 0 && (
        <div className="flex items-baseline justify-between mb-4" data-testid="custom-set-subtotal">
          <span className="text-[13px] text-neutral-500">Subtotal</span>
          <div className="flex items-baseline gap-2">
            {discountPct > 0 && (
              <span className="text-[12px] text-neutral-400 line-through font-mono tabular-nums">
                ${subtotal.toFixed(2)}
              </span>
            )}
            <span className="text-[18px] font-medium text-neutral-900 font-mono tabular-nums">
              ${total.toFixed(2)}
            </span>
          </div>
        </div>
      )}

      <button
        type="button"
        disabled={count === 0}
        data-testid="custom-set-add-to-cart"
        className={`w-full py-4 text-xs tracking-[0.12em] uppercase font-medium transition-colors ${
          count > 0
            ? "bg-riversol-blue text-white hover:bg-riversol-blueLight"
            : "bg-[#9DB3B3] text-white cursor-not-allowed"
        }`}
      >
        {count > 0 ? `Add To Cart — $${total.toFixed(2)}` : "Add To Cart"}
      </button>
    </div>
  );
};


export const BundleBuilder = () => {
  const P = useProduct();
  const { routine, v32, v34 } = P;
  const products = routine.products;
  const baseProduct = v34
    ? { name: P.titleLines.join(" "), image: P.gallery[0].src, price: P.price.regular }
    : null;
  const [selected, setSelected] = useState(() => new Set());
  const toggle = (i) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else if (!baseProduct || next.size < 4) next.add(i);
      return next;
    });

  // Mirror BundleDeck's derived values so the mobile sticky bar can show
  // the same dynamic "add X more" hint and total without prop-drilling.
  const count = selected.size;
  const subtotal = useMemo(
    () =>
      [...selected]
        .map((i) => products[i])
        .filter(Boolean)
        .reduce((s, p) => s + parsePrice(p.price), 0),
    [selected, products]
  );
  const tiers = v32 ? TIERS_V32 : TIERS;
  const tierIdx = tiers.reduce(
    (acc, t, i) => (count >= t.count ? i : acc),
    -1
  );
  const discountPct = v32 ? discountForV32(count) : tierIdx >= 0 ? 0.1 : 0;
  const total = subtotal * (1 - discountPct);
  const nextTier = tiers.find((t) => count < t.count);

  return (
    <section className="bg-white py-32" data-testid="complete-routine">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* LEFT — sticky column: heading (over image) + Bundle Deck must
              both remain visible at ALL desktop heights. Structure:
              `sticky top-24` with `max-h-[calc(100vh-6rem)]` clamps the
              column to viewport height; a flex-col inside lets the image
              shrink/grow (`flex-1`, with a min-height so the heading stays
              legible) while the Bundle Deck holds its natural height and
              never shrinks. Effect: on tall screens the image is big; on
              short laptops the image shrinks but the heading + full deck
              stay locked to the fold. */}
          <div className="lg:col-span-5">
            {v32 ? (
              <div className="lg:sticky lg:top-24">
                <CustomSetPanel
                  selected={selected}
                  products={products}
                  onRemove={toggle}
                  baseProduct={baseProduct}
                  hideEyebrow={v34}
                />
              </div>
            ) : (
              <div className="lg:sticky lg:top-24 flex flex-col gap-4 lg:h-[calc(100vh-8rem)] lg:max-h-[720px]">
                <div
                  className="relative editorial-frame group bg-riversol-sand overflow-hidden flex-1 min-h-0"
                  data-testid="bundle-hero-image"
                >
                  <img
                    src={routine.lifestyleImage}
                    alt={routine.lifestyleAlt}
                    className="w-full h-full object-cover img-zoom absolute inset-0"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
                  <div className="relative z-10 h-full p-8 flex flex-col justify-end text-white">
                    <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-white/70 mb-3">
                      {routine.eyebrow}
                    </div>
                    <h2 className="leading-[1.03] tracking-tight font-semibold text-4xl lg:text-5xl max-w-lg">
                      {routine.title}
                    </h2>
                  </div>
                </div>
                <div className="shrink-0">
                  <BundleDeck
                    selected={selected}
                    products={products}
                    onRemove={toggle}
                  />
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — product list with toggle-into-bundle buttons. Reuses
              the V2 horizontal-row treatment; the left-edge accent stripe
              appears when a row is in the bundle. */}
          <div className="lg:col-span-7 flex flex-col">
            {!v34 && (
              <div className="mb-6 px-1 flex items-baseline justify-between gap-4">
                <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-neutral-500">
                  Choose your products
                </div>
                <div className="font-mono text-[10px] tracking-[0.14em] uppercase text-neutral-400">
                  {products.length} products
                </div>
              </div>
            )}
            <div className="space-y-4">
              {products.map((p, i) => {
                const active = selected.has(i);
                return (
                  <Reveal key={p.name} delay={i * 0.05}>
                    <div
                      data-testid={`bundle-row-${i}`}
                      data-active={active}
                      className={`relative flex gap-6 items-center bg-white border p-5 transition-all duration-300 ${
                        active
                          ? "border-riversol-blue"
                          : "border-neutral-200 hover:border-neutral-400"
                      }`}
                    >
                      {/* Accent stripe when active */}
                      <span
                        aria-hidden="true"
                        className={`absolute left-0 top-0 bottom-0 w-1 bg-riversol-blue transition-opacity ${
                          active ? "opacity-100" : "opacity-0"
                        }`}
                      />
                      <div className="w-32 h-32 shrink-0 bg-riversol-sand editorial-frame flex items-center justify-center border border-neutral-200 overflow-hidden" style={{ clipPath: `inset(0 round ${P.mediaRadius || "15px"})` }}>
                        {p.image ? (
                          <img
                            src={p.image}
                            alt={p.name}
                            className="w-full h-full object-cover img-zoom"
                          />
                        ) : (
                          <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-neutral-400 text-center px-4 leading-relaxed">
                            {p.name}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 flex flex-col">
                        <h3 className="text-lg text-neutral-900 leading-snug mb-1.5">
                          {p.name}
                        </h3>
                        <p className="text-[13.5px] text-neutral-500 leading-relaxed flex-1 max-w-lg">
                          {p.reason}
                        </p>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-neutral-100">
                          <span className="font-mono text-sm text-neutral-900">
                            {p.price}
                          </span>
                          <button
                            type="button"
                            onClick={() => toggle(i)}
                            data-testid={`bundle-toggle-${i}`}
                            aria-pressed={active}
                            className={`inline-flex items-center gap-1.5 font-mono text-[11px] tracking-[0.14em] uppercase transition-all rounded-full px-4 py-2 ${
                              active
                                ? "bg-riversol-blue text-white"
                                : "border border-neutral-300 text-neutral-800 hover:border-riversol-blue hover:text-riversol-blue"
                            }`}
                          >
                            {active ? (
                              <>
                                <Check size={13} strokeWidth={2.5} />
                                In bundle
                              </>
                            ) : (
                              <>
                                <Plus size={13} strokeWidth={2} />
                                Add
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>

        {/* Mobile-only sticky bundle bar. Sticks to viewport bottom while
            the user scrolls through the product list, then releases once
            they've scrolled past the last product (containing block ends).
            Offset above the persistent StickyCartBar (64px on mobile) so
            both remain readable. Hidden on lg+ because the desktop
            BundleDeck is already sticky. */}
        <div
          className="lg:hidden sticky bottom-[76px] z-40 mt-6 pointer-events-none"
          data-testid="bundle-mobile-cta-bar"
        >
          <div className="pointer-events-auto bg-white/95 backdrop-blur border border-neutral-200 shadow-[0_8px_24px_rgba(15,46,46,0.12)] rounded-full pl-4 pr-1.5 py-1.5 flex items-center gap-3">
            <div className="flex-1 min-w-0 flex flex-col gap-1 py-1">
              {count === 0 && (
                <>
                  <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-neutral-500 leading-none">
                    Start your bundle
                  </div>
                  <div className="text-[13px] text-neutral-800 truncate leading-tight">
                    Add 2+ to unlock rewards
                  </div>
                </>
              )}
              {count > 0 && nextTier && (
                <>
                  <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-neutral-500 truncate leading-none">
                    {count} / {MAX} · ${total.toFixed(2)}
                  </div>
                  <div className="text-[13px] text-neutral-800 truncate leading-tight">
                    Add {nextTier.count - count} more for{" "}
                    <span className="text-riversol-blue font-medium">
                      {nextTier.label.replace(/^\+ /, "")}
                    </span>
                  </div>
                </>
              )}
              {count > 0 && !nextTier && (
                <>
                  <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-riversol-blue leading-none">
                    All rewards unlocked
                  </div>
                  <div className="text-[13px] text-neutral-800 truncate leading-tight">
                    Bundle total ${total.toFixed(2)}
                  </div>
                </>
              )}
            </div>
            <button
              type="button"
              disabled={count === 0}
              data-testid="bundle-mobile-add-to-cart"
              className={`shrink-0 rounded-full px-4 py-2.5 text-[11px] tracking-[0.14em] uppercase font-medium transition-colors ${
                count > 0
                  ? "bg-riversol-blue text-white hover:bg-riversol-blueLight"
                  : "bg-neutral-100 text-neutral-400 cursor-not-allowed"
              }`}
            >
              {count > 0 ? `Add · $${total.toFixed(2)}` : "Start"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BundleBuilder;
